<template>
    <div
        v-if="type === 'select'"
        class="filter-chip-container"
        :class="{ 'chip-container--removable': removable, 'chip-container--clearable': clearable }"
        :data-filter-field="dataFilterField"
    >
        <MinimalPopoverSelect
            ref="popoverSelect"
            :value="value"
            :valueText="valueText"
            :options="options"
            :multiple="multiple"
            :filter-name="filterName"
            :show-icon="true"
            :enable-search="enableSearch"
            :show-dropdown-arrow="false"
            :button-class="chipClass + (removable ? ' chip--removable' : '') + (hasValue ? ' chip--has-value' : '')"
            popover-class="filter-select-popover"
            check-icon="done"
            :default-icon="icon"
            :removable="removable"
            :clearable="isClearable"
            @input="onInput"
            @change="onChange"
            @search-input="onSearchInput"
            @remove-click="onRemove"
            @clear-click="onClear"
        />
    </div>

    <div
        v-else-if="type === 'text'"
        class="filter-chip-container"
        :class="{ 'chip-container--removable': removable, 'chip-container--clearable': clearable }"
        :data-filter-field="dataFilterField"
    >
        <CustomPopover
            ref="customPopover"
            :icon="icon"
            :label="getLabel()"
            :show-dropdown-arrow="false"
            :button-class="chipClass + (removable ? ' chip--removable' : '') + (hasValue ? ' chip--has-value' : '')"
            popover-class="filter-select-popover"
            :show-form-footer="true"
            :removable="removable"
            :clearable="isClearable"
            @popover-change="onPopoverChange"
            @clear="onPopoverClear"
            @apply="onPopoverApply"
            @remove-click="onRemove"
            @clear-click="onClear"
        >
            <template slot="content">
                <div class="operator-section" v-if="operatorSelectable">
                    <label class="operator-label">{{ $t('chat.filterChip.operator') }}</label>
                    <select v-model="currentOperator" class="operator-select">
                        <option v-for="option in textOperatorOptions" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </option>
                    </select>
                </div>
                <div class="input-container">
                    <input
                        type="text"
                        :value="value"
                        :placeholder="$t('chat.filterChip.enterText')"
                        class="popover-input"
                        @input="onInputChange"
                        @blur="onInputBlur"
                    />
                </div>
            </template>
        </CustomPopover>
    </div>

    <div
        v-else-if="type === 'number'"
        class="filter-chip-container"
        :class="{ 'chip-container--removable': removable, 'chip-container--clearable': clearable }"
        :data-filter-field="dataFilterField"
    >
        <CustomPopover
            :icon="icon"
            :label="getLabel()"
            :show-dropdown-arrow="false"
            :button-class="chipClass + (removable ? ' chip--removable' : '') + (hasValue ? ' chip--has-value' : '')"
            :show-form-footer="true"
            :removable="removable"
            :clearable="isClearable"
            @popover-change="onPopoverChange"
            @clear="onPopoverClear"
            @apply="onPopoverApply"
            @remove-click="onRemove"
            @clear-click="onClear"
        >
            <template slot="content">
                <div class="operator-section" v-if="operatorSelectable">
                    <label class="operator-label">{{ $t('chat.filterChip.operator') }}</label>
                    <select v-model="currentOperator" class="operator-select">
                        <option v-for="option in numberOperatorOptions" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </option>
                    </select>
                </div>
                <div class="input-container">
                    <input
                        type="number"
                        :value="value"
                        :placeholder="$t('chat.filterChip.enterNumber')"
                        class="popover-input"
                        @input="onInputChange"
                        @blur="onInputBlur"
                    />
                </div>
            </template>
        </CustomPopover>
    </div>

    <div
        v-else-if="type === 'date'"
        class="filter-chip-container"
        :class="{ 'chip-container--removable': removable, 'chip-container--clearable': clearable }"
        :data-filter-field="dataFilterField"
    >
        <CustomPopover
            ref="customPopover"
            :icon="icon"
            :label="getLabel()"
            :show-dropdown-arrow="false"
            :button-class="chipClass + (removable ? ' chip--removable' : '') + (hasValue ? ' chip--has-value' : '')"
            :show-form-footer="true"
            :removable="removable"
            :clearable="isClearable"
            @popover-change="onPopoverChange"
            @clear="onPopoverClear"
            @apply="onPopoverApply"
            @remove-click="onRemove"
            @clear-click="onClear"
        >
            <template slot="content">
                <div class="operator-section" v-if="operatorSelectable">
                    <label class="operator-label">{{ $t('chat.filterChip.operator') }}</label>
                    <select v-model="currentOperator" class="operator-select" @change="onOperatorChange">
                        <option v-for="option in dateOperatorOptions" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </option>
                    </select>
                </div>
                <div class="input-container" v-if="currentOperator === 'between'">
                    <div class="date-preset-container">
                        <div class="date-preset-list">
                            <div
                                v-for="preset in datePresetOptions"
                                :key="preset.value"
                                class="date-preset-item"
                                :class="{ 'date-preset-item--active': selectedDatePreset === preset.value }"
                                @click="onDatePresetSelect(preset.value)"
                            >
                                {{ preset.label }}
                            </div>
                        </div>
                    </div>
                    <div v-if="selectedDatePreset === 'custom'" class="date-custom-container">
                        <a-range-picker
                            :value="customDateRange"
                            format="YYYY-MM-DD"
                            class="popover-range-picker"
                            @change="onDateRangeChange"
                        />
                    </div>
                </div>
                <div class="input-container" v-else>
                    <a-date-picker
                        :value="singleDateValue"
                        format="YYYY-MM-DD"
                        class="popover-date-picker"
                        @change="onSingleDateChange"
                    />
                </div>
            </template>
        </CustomPopover>
    </div>

    <div
        v-else-if="type === 'composite'"
        class="filter-chip-container"
        :class="{ 'chip-container--removable': removable, 'chip-container--clearable': clearable }"
        :data-filter-field="dataFilterField"
    >
        <CustomPopover
            ref="customPopover"
            :icon="icon"
            :label="getCompositeLabel()"
            :show-dropdown-arrow="false"
            :button-class="chipClass + (removable ? ' chip--removable' : '') + (hasValue ? ' chip--has-value' : '')"
            :show-form-footer="true"
            :removable="removable"
            :clearable="isClearable"
            @popover-change="onPopoverChange"
            @clear="onPopoverClear"
            @apply="onPopoverApply"
            @remove-click="onRemove"
            @clear-click="onClear"
        >
            <template slot="content">
                <div class="composite-container">
                    <div class="composite-field">
                        <label class="composite-label">{{ compositeSelectLabel || $t('chat.filterChip.type') }}:</label>
                        <select v-model="compositeSelectValue" class="composite-select">
                            <option v-for="option in options" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                    <div class="composite-field">
                        <label class="composite-label">{{ compositeInputLabel || $t('chat.filterChip.value') }}:</label>
                        <input
                            type="text"
                            v-model="compositeInputValue"
                            :placeholder="compositeInputPlaceholder || $t('chat.filterChip.enterValue')"
                            class="composite-input"
                        />
                    </div>
                </div>
            </template>
        </CustomPopover>
    </div>

    <BroadcastCompositeFilterChip
        v-else-if="type === 'broadcast-composite'"
        ref="broadcastComposite"
        :value="value"
        :filter-name="filterName"
        :icon="icon"
        :removable="removable"
        :clearable="clearable"
        :data-filter-field="dataFilterField"
        @change="onChange"
        @remove="onRemove"
    />

    <div v-else-if="type === 'add'">
        <MinimalPopoverSelect
            ref="popoverSelect"
            :value="value"
            :options="options"
            :multiple="multiple"
            :enable-search="enableSearch"
            :show-icon="false"
            :show-dropdown-arrow="false"
            check-icon="done"
            @input="onInput"
            @change="onChange"
            @search-input="onSearchInput"
        >
            <template v-slot:trigger>
                <div class="chip filter-chip chip--add">
                    <i class="material-icons">add</i>
                    <span>{{ label || $t('chat.filterChip.addFilter') }}</span>
                </div>
            </template>
        </MinimalPopoverSelect>
    </div>
</template>

<script>
import MinimalPopoverSelect from "./MinimalPopoverSelect.vue";
import CustomPopover from "./CustomPopover.vue";
import BroadcastCompositeFilterChip from "./BroadcastCompositeFilterChip.vue";

export default {
    name: "FilterChip",
    components: {
        MinimalPopoverSelect,
        CustomPopover,
        BroadcastCompositeFilterChip,
    },
    props: {
        type: {
            type: String,
            required: true,
            validator: (value) =>
                ["select", "toggle", "removable", "add", "text", "number", "date", "composite", "broadcast-composite"].includes(value),
        },
        value: {
            type: [String, Array, Boolean, Number, Object],
            default: null,
        },
        valueText: {
            type: String,
            default: "",
        },
        options: {
            type: Array,
            default: () => [],
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        icon: {
            type: String,
            default: "filter_list",
        },
        label: {
            type: String,
            default: "",
        },
        removable: {
            type: Boolean,
            default: false,
        },
        filterName: {
            type: String,
            default: "",
        },
        dataFilterField: {
            type: String,
            default: "",
        },
        enableSearch: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        operatorSelectable: {
            type: Boolean,
            default: false,
        },
        operator: {
            type: String,
            default: null,
        },
        compositeSelectLabel: {
            type: String,
            default: null,
        },
        compositeInputLabel: {
            type: String,
            default: null,
        },
        compositeInputPlaceholder: {
            type: String,
            default: null,
        },
    },
    computed: {
        chipClass() {
            return "chip filter-chip";
        },
        hasValue() {
            // Date tipinde selectedDatePreset varsa değer var kabul et
            if (this.type === "date" && this.selectedDatePreset) {
                return true;
            }

            // Composite tipinde selectValue ve inputValue varsa değer var kabul et
            if (this.type === "composite") {
                if (this.value && typeof this.value === "object") {
                    return !!(this.value.selectValue && this.value.inputValue);
                }
                return false;
            }

            if (this.value === undefined || this.value === null || this.value === "") {
                return false;
            }
            if (Array.isArray(this.value) && this.value.length === 0) {
                return false;
            }
            return true;
        },
        isClearable() {
            return (
                this.clearable &&
                this.value !== undefined &&
                this.value !== null &&
                this.value !== "" &&
                (Array.isArray(this.value) ? this.value.length > 0 : true)
            );
        },
        textOperatorOptions() {
            return [
                { label: this.$t('chat.filterChip.equals'), value: "equals" },
                { label: this.$t('chat.filterChip.contains'), value: "contains" },
                { label: this.$t('chat.filterChip.startsWith'), value: "starts_with" },
                { label: this.$t('chat.filterChip.endsWith'), value: "ends_with" }
            ];
        },
        numberOperatorOptions() {
            return [
                { label: this.$t('chat.filterChip.equals'), value: "equals" },
                { label: this.$t('chat.filterChip.greaterThan'), value: "greater_than" },
                { label: this.$t('chat.filterChip.lessThan'), value: "less_than" },
                { label: this.$t('chat.filterChip.greaterThanOrEqual'), value: "greater_than_or_equal" },
                { label: this.$t('chat.filterChip.lessThanOrEqual'), value: "less_than_or_equal" },
            ];
        },
        dateOperatorOptions() {
            return [
                { label: this.$t('chat.filterChip.between'), value: "between" },
                { label: this.$t('chat.filterChip.equals'), value: "equals" },
                { label: this.$t('chat.filterChip.greaterThanOrEqual'), value: "greater_than_or_equal" },
                { label: this.$t('chat.filterChip.lessThanOrEqual'), value: "less_than_or_equal" },
                { label: this.$t('chat.filterChip.greaterThan'), value: "greater_than" },
                { label: this.$t('chat.filterChip.lessThan'), value: "less_than" }
            ];
        },
        datePresetOptions() {
            // Custom en üste
            const options = [{ label: this.$t('chat.filterChip.custom'), value: "custom" }];

            // Diğer seçenekler
            options.push(
                { label: this.$t('chat.filtersSection.today'), value: "today" },
                { label: this.$t('chat.filtersSection.yesterday'), value: "yesterday" },
                { label: this.$t('chat.filtersSection.thisWeek'), value: "thisWeek" },
                { label: this.$t('chat.filtersSection.lastWeek'), value: "lastWeek" },
                { label: this.$t('chat.filtersSection.last7Days'), value: "last7Days" },
                { label: this.$t('chat.filtersSection.last30Days'), value: "last30Days" },
                { label: this.$t('chat.filtersSection.thisMonth'), value: "thisMonth" },
                { label: this.$t('chat.filtersSection.lastMonth'), value: "lastMonth" }
            );

            // Bu ay hariç geriye dönük son 11 ay (toplam 12 ay)
            for (let i = 2; i <= 12; i++) {
                const monthMoment = this.$moment().subtract(i, "month");
                options.push({
                    label: monthMoment.format("MMMM YYYY"),
                    value: `month${i}`,
                });
            }

            return options;
        },
    },
    data() {
        return {
            currentOperator: "equals",
            compositeSelectValue: null,
            compositeInputValue: "",
            selectedDatePreset: null,
            customDateRange: null,
            singleDateValue: null,
        };
    },
    methods: {
        getLabel() {
            if (this.valueText) {
                return this.filterName ? `${this.filterName}: ${this.valueText}` : this.valueText;
            }

            // Date tipinde label göster
            if (this.type === "date") {
                if (this.currentOperator === "between") {
                    const relativePresets = ["today", "yesterday", "thisWeek", "lastWeek", "last7Days", "last30Days", "thisMonth", "lastMonth"];

                    if (typeof this.value === "string" && relativePresets.includes(this.value)) {
                        const preset = this.datePresetOptions.find((p) => p.value === this.value);
                        if (preset) {
                            return this.filterName ? `${this.filterName}: ${preset.label}` : preset.label;
                        }
                    }

                    if (this.selectedDatePreset) {
                        const preset = this.datePresetOptions.find((p) => p.value === this.selectedDatePreset);
                        if (preset) {
                            if (preset.value === "custom" && this.customDateRange) {
                                // Custom seçildiyse tarih aralığını göster
                                if (this.$moment && Array.isArray(this.customDateRange) && this.customDateRange.length === 2) {
                                    const startDate = this.$moment(this.customDateRange[0]).format("DD MMM YYYY");
                                    const endDate = this.$moment(this.customDateRange[1]).format("DD MMM YYYY");
                                    const dateRange = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
                                    return this.filterName ? `${this.filterName}: ${dateRange}` : dateRange;
                                }
                            } else if (preset.value !== "custom") {
                                return this.filterName ? `${this.filterName}: ${preset.label}` : preset.label;
                            }
                        }
                    }
                } else if (this.singleDateValue) {
                    // Tek tarih operatörleri için
                    const formattedDate = this.$moment ? this.$moment(this.singleDateValue).format("DD MMM YYYY") : this.singleDateValue;
                    return this.filterName ? `${this.filterName}: ${formattedDate}` : formattedDate;
                }
            }

            if (this.value !== undefined && this.value !== null && this.value !== "") {
                const displayValue = Array.isArray(this.value) && this.value.length > 0 ? this.value : this.value;
                return this.filterName ? `${this.filterName}: ${displayValue}` : displayValue;
            }

            return this.filterName || this.$t('chat.filterChip.select');
        },
        getCompositeLabel() {
            if (this.value && this.value.selectValue && this.value.inputValue) {
                const optionLabel = this.options.find((opt) => opt.value === this.value.selectValue);
                const selectLabel = optionLabel ? optionLabel.label : this.value.selectValue;
                return this.filterName
                    ? `${this.filterName}: ${selectLabel} - ${this.value.inputValue}`
                    : `${selectLabel} - ${this.value.inputValue}`;
            }
            return this.filterName || this.$t('chat.filterChip.select');
        },
        onInput(value) {
            this.$emit("input", value);
        },
        onChange(value) {
            this.$emit("change", value);
        },
        onRemove() {
            this.$emit("remove");
        },
        onAdd() {
            this.$emit("add");
        },
        onSearchInput(query) {
            this.$emit("search-input", query);
        },
        onClear() {
            this.$emit("clear");
        },
        onInputChange(event) {
            const value = event.target.value;
            this.$emit("input", value);
        },
        onInputBlur(event) {
            const value = event.target.value;
            this.$emit("change", value);
        },
        onPopoverChange(visible) {
            // Popover açılıp kapandığında yapılacak işlemler
        },
        onPopoverClear() {
            if (this.type === "composite") {
                this.compositeSelectValue = null;
                this.compositeInputValue = "";
                this.$emit("change", null);
            } else if (this.type === "date") {
                if (this.currentOperator === "between") {
                    this.selectedDatePreset = null;
                    this.customDateRange = null;
                } else {
                    this.singleDateValue = null;
                }
                this.$emit("change", null);
            } else {
                this.$emit("change", "");
            }
        },
        onPopoverApply() {
            if (this.type === "composite") {
                const compositeValue = {
                    selectValue: this.compositeSelectValue,
                    inputValue: this.compositeInputValue,
                };
                this.$emit("change", compositeValue);
            } else if (this.type === "date" && this.currentOperator === "between") {
                if (this.selectedDatePreset === "custom") {
                    this.$emit("change", this.customDateRange);
                } else if (this.selectedDatePreset) {
                    const relativePresets = ["today", "yesterday", "thisWeek", "lastWeek", "last7Days", "last30Days", "thisMonth", "lastMonth"];
                    if (relativePresets.includes(this.selectedDatePreset)) {
                        this.$emit("change", this.selectedDatePreset);
                    } else {
                        const dateRange = this.getDateRangeFromPreset(this.selectedDatePreset);
                        this.$emit("change", dateRange);
                    }
                }
            } else if (this.type === "date") {
                // Tek tarih seçimi (greater_than, less_than vb.)
                this.$emit("change", this.singleDateValue);
            } else {
                this.$emit("change", this.value);
            }
        },
        onDateRangeChange(dates) {
            this.customDateRange = dates;
        },
        onSingleDateChange(date) {
            this.singleDateValue = date;
        },
        isRelativeDatePreset(presetValue) {
            const relativePresets = ["today", "yesterday", "thisWeek", "lastWeek", "last7Days", "last30Days", "thisMonth", "lastMonth"];
            return relativePresets.includes(presetValue);
        },
        openPopover() {
            if (this.$refs.popoverSelect) {
                this.$refs.popoverSelect.openPopover();
            } else if (this.$refs.customPopover) {
                this.$refs.customPopover.openPopover();
            } else if (this.$refs.broadcastComposite) {
                this.$refs.broadcastComposite.openPopover();
            }
        },
        closePopover() {
            if (this.$refs.popoverSelect) {
                this.$refs.popoverSelect.closePopover();
            } else if (this.$refs.customPopover) {
                this.$refs.customPopover.closePopover();
            } else if (this.$refs.broadcastComposite) {
                this.$refs.broadcastComposite.closePopover();
            }
        },
        onOperatorChange(event) {
            // Event'ten geliyorsa (kullanıcı select'ten değiştirdi)
            if (event && event.target) {
                this.currentOperator = event.target.value;
            }
            
            this.$emit("operator-change", this.currentOperator);
            
            if (this.currentOperator === "between") {
                // Between operatörü seçildiğinde varsayılan olarak "Bugün" seç (sadece görsel olarak)
                if (!this.selectedDatePreset) {
                    this.selectedDatePreset = "today";
                }
                this.singleDateValue = null;
            } else {
                // Tek tarih operatörleri için
                this.selectedDatePreset = null;
                this.customDateRange = null;
                if (!this.singleDateValue) {
                    this.singleDateValue = this.$moment ? this.$moment() : null;
                }
            }
        },
        onDatePresetSelect(presetValue) {
            this.selectedDatePreset = presetValue;
            if (presetValue === "custom") {
                // Custom seçildiğinde mevcut value'yu customDateRange'e aktar
                if (this.value && Array.isArray(this.value) && this.value.length === 2) {
                    this.customDateRange = this.value;
                } else {
                    this.customDateRange = null;
                }
            }
        },
        updateDateValueFromPreset(presetValue) {
            const relativePresets = ["today", "yesterday", "thisWeek", "lastWeek", "last7Days", "last30Days", "thisMonth", "lastMonth"];
            if (relativePresets.includes(presetValue)) {
                this.$emit("change", presetValue);
            } else {
                const dateRange = this.getDateRangeFromPreset(presetValue);
                if (dateRange) {
                    this.$emit("change", dateRange);
                }
            }
        },
        getDateRangeFromPreset(presetValue) {
            if (!presetValue || !this.$moment) {
                return null;
            }

            const moment = this.$moment;
            let startDate, endDate;

            switch (presetValue) {
                case "today":
                    startDate = moment().startOf("day");
                    endDate = moment().endOf("day");
                    break;
                case "yesterday":
                    startDate = moment().subtract(1, "day").startOf("day");
                    endDate = moment().subtract(1, "day").endOf("day");
                    break;
                case "thisWeek":
                    startDate = moment().startOf("isoWeek");
                    endDate = moment().endOf("isoWeek");
                    break;
                case "lastWeek":
                    startDate = moment().subtract(1, "week").startOf("isoWeek");
                    endDate = moment().subtract(1, "week").endOf("isoWeek");
                    break;
                case "last7Days":
                    startDate = moment().subtract(7, "day").startOf("day");
                    endDate = moment().endOf("day");
                    break;
                case "last30Days":
                    startDate = moment().subtract(30, "day").startOf("day");
                    endDate = moment().endOf("day");
                    break;
                case "thisMonth":
                    startDate = moment().startOf("month");
                    endDate = moment().endOf("month");
                    break;
                case "lastMonth":
                    startDate = moment().subtract(1, "month").startOf("month");
                    endDate = moment().subtract(1, "month").endOf("month");
                    break;
                default:
                    // month2, month3, ..., month12 için
                    if (presetValue.startsWith("month")) {
                        const monthIndex = parseInt(presetValue.replace("month", ""), 10);
                        startDate = moment().subtract(monthIndex, "month").startOf("month");
                        endDate = moment().subtract(monthIndex, "month").endOf("month");
                    } else {
                        return null;
                    }
                    break;
            }

            return [startDate, endDate];
        },
        detectDatePresetFromValue() {
            const relativePresets = ["today", "yesterday", "thisWeek", "lastWeek", "last7Days", "last30Days", "thisMonth", "lastMonth"];

            if (typeof this.value === "string" && relativePresets.includes(this.value)) {
                this.selectedDatePreset = this.value;
                return;
            }

            if (!this.value || !Array.isArray(this.value) || this.value.length !== 2 || !this.$moment) {
                this.selectedDatePreset = "custom";
                this.customDateRange = this.value;
                return;
            }

            const moment = this.$moment;
            const startDate = moment(this.value[0]);
            const endDate = moment(this.value[1]);

            // Bugün kontrolü
            if (startDate.isSame(moment().startOf("day")) && endDate.isSame(moment().endOf("day"))) {
                this.selectedDatePreset = "today";
                return;
            }

            // Dün kontrolü
            if (
                startDate.isSame(moment().subtract(1, "day").startOf("day")) &&
                endDate.isSame(moment().subtract(1, "day").endOf("day"))
            ) {
                this.selectedDatePreset = "yesterday";
                return;
            }

            // Bu Hafta kontrolü
            if (startDate.isSame(moment().startOf("isoWeek")) && endDate.isSame(moment().endOf("isoWeek"))) {
                this.selectedDatePreset = "thisWeek";
                return;
            }

            // Geçen Hafta kontrolü
            if (
                startDate.isSame(moment().subtract(1, "week").startOf("isoWeek")) &&
                endDate.isSame(moment().subtract(1, "week").endOf("isoWeek"))
            ) {
                this.selectedDatePreset = "lastWeek";
                return;
            }

            // Son 7 Gün kontrolü
            if (
                startDate.isSame(moment().subtract(7, "day").startOf("day")) &&
                endDate.isSame(moment().endOf("day"))
            ) {
                this.selectedDatePreset = "last7Days";
                return;
            }

            // Son 30 Gün kontrolü
            if (
                startDate.isSame(moment().subtract(30, "day").startOf("day")) &&
                endDate.isSame(moment().endOf("day"))
            ) {
                this.selectedDatePreset = "last30Days";
                return;
            }

            // Bu Ay kontrolü
            if (startDate.isSame(moment().startOf("month")) && endDate.isSame(moment().endOf("month"))) {
                this.selectedDatePreset = "thisMonth";
                return;
            }

            // Geçen Ay kontrolü
            if (
                startDate.isSame(moment().subtract(1, "month").startOf("month")) &&
                endDate.isSame(moment().subtract(1, "month").endOf("month"))
            ) {
                this.selectedDatePreset = "lastMonth";
                return;
            }

            // Aylık kontroller (month2-month12)
            for (let i = 2; i <= 12; i++) {
                if (
                    startDate.isSame(moment().subtract(i, "month").startOf("month")) &&
                    endDate.isSame(moment().subtract(i, "month").endOf("month"))
                ) {
                    this.selectedDatePreset = `month${i}`;
                    return;
                }
            }

            // Hiçbiri eşleşmezse custom
            this.selectedDatePreset = "custom";
            this.customDateRange = this.value;
        },
    },
    mounted() {
        // Operator'ı set et
        if (this.operatorSelectable) {
            // Prop'tan gelen operator varsa onu kullan
            if (this.operator) {
                this.currentOperator = this.operator;
            } else {
                // Default operator'ı set et
                const defaultOperators = {
                    text: "equals",
                    number: "equals",
                    date: "between",
                };
                if (defaultOperators[this.type]) {
                    this.currentOperator = defaultOperators[this.type];
                }
            }
        }

        if (this.type === "composite" && this.value) {
            this.compositeSelectValue =
                this.value.selectValue || (this.options.length > 0 ? this.options[0].value : null);
            this.compositeInputValue = this.value.inputValue || "";
        } else if (this.type === "composite" && this.options.length > 0) {
            this.compositeSelectValue = this.options[0].value;
        }

        // Date tipinde value'ları set et
        if (this.type === "date") {
            if (this.currentOperator === "between" && this.value) {
                this.detectDatePresetFromValue();
            } else if (this.currentOperator !== "between" && this.value) {
                // Tek tarih operatörleri için value'yu set et
                this.singleDateValue = this.value;
            }
        }
    },
};
</script>

<style lang="scss">
.filter-chip-container {
    display: inline-flex;
    align-items: center;
    position: relative;
    gap: 0rem;

    &--removable,
    &--clearable {
        .chip {
            padding-right: 0.5rem;
            padding-left: 0.5rem;
            gap: 0.3rem;
            min-width: fit-content;
        }
    }
}

.chip {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.25rem 0.5rem;
    border: 1px solid #c7d0d7;
    border-radius: 999px;
    background: #fff;
    color: #22272d;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;
    font-weight: 600;
    min-width: fit-content;

    &.chip--has-value {
        background: #f3f4f6;
        border-color: #d1d5db;
    }

    &:hover {
        background: #f0f0f0;
        color: #22272d;

        .material-icons {
            color: #22272d !important;
        }
    }

    &--add {
        border-style: dashed;
        border-color: #9ca3af;
        background: #f9fafb;
        color: #6b7280;
        font-weight: 600;

        &:hover {
            border-style: solid;
            border-color: #22272d;
            background: #f3f4f6;
            color: #22272d;

            .material-icons {
                color: #22272d;
            }
        }

        .material-icons {
            color: #6b7280;
        }
    }

    .material-icons {
        font-size: 18px;
        line-height: 1;
        color: #22272d;
        flex-shrink: 0;
    }

    strong {
        font-weight: 700;
    }

    span {
        margin-right: 0.3rem;
    }
}

.chip-input {
    border: none;
    outline: none;
    background: transparent;
    color: #22272d;
    font-weight: 600;
    font-size: 14px;
    min-width: 80px;
    max-width: 120px;

    &::placeholder {
        color: #9ca3af;
        font-weight: 400;
    }

    &:focus {
        outline: none;
    }
}

.operator-section {
    padding: 12px;
    border-bottom: 1px solid #e5e7eb;
    background: #f8f9fa;
}

.operator-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
}

.operator-select {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 13px;
    background: #fff;

    &:focus {
        outline: none;
        border-color: #22272d;
        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
    }
}

.input-container {
    padding: 12px;
}

.popover-input {
    width: 100%;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 14px;
    outline: none;

    &:focus {
        border-color: #22272d;
        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
    }

    &::placeholder {
        color: #9ca3af;
    }
}

.popover-range-picker,
.popover-date-picker {
    width: 100%;

    .ant-calendar-picker-input {
        border: 1px solid #d9d9d9;
        border-radius: 4px;

        &:focus {
            border-color: #22272d;
            box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
        }
    }
}

.date-preset-container {
    padding: 8px 0;
}

.date-preset-list {
    max-height: 300px;
    overflow-y: auto;
}

.date-preset-item {
    padding: 10px 12px;
    cursor: pointer;
    font-size: 14px;
    color: #374151;
    transition: all 0.15s ease;
    border-left: 3px solid transparent;

    &:hover {
        background: #f0f0f0;
        color: #22272d;
    }

    &--active {
        background: #f0f0f0;
        color: #22272d;
        border-left-color: #22272d;
        font-weight: 600;
    }
}

.date-custom-container {
    padding: 8px 0;
    margin-top: 8px;
    border-top: 1px solid #e5e7eb;
}

.chip-remove,
.chip-clear {
    display: none;
}

.composite-container {
    padding: 12px;
    min-width: 300px;
}

.composite-field {
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }
}

.composite-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
}

.composite-select,
.composite-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 13px;
    background: #fff;
    outline: none;

    &:focus {
        border-color: #22272d;
        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
    }
}

.composite-input::placeholder {
    color: #9ca3af;
}

/* Filter select popover styles */
.filter-select-popover {
    margin-top: -10px;

    .ant-popover-arrow {
        display: none;
    }

    .ant-popover-inner-content {
        padding: 0px;
        border: 1px solid #c7d0d7;
        border-radius: 5px;
    }

    .modern-select-dropdown {
        min-width: 150px;
        background: #fff;
    }

    .select-option {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 40px;
        padding: 8px 12px;
        cursor: pointer;
        color: #22272d;
        transition: all 0.15s ease;

        &:hover {
            background: #f0f0f0;
        }

        &.active {
            background: #f0f0f0;
            font-weight: 600;
        }
    }

    .select-option .option-content {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;
    }

    .select-option .option-icon {
        font-size: 16px;
        color: #22272d;
    }

    .select-option .option-label {
        flex: 1;
        font-size: 14px;
    }

    .select-option .check-icon {
        margin-left: auto;
        font-size: 16px;
        color: #22272d;
    }
}
</style>
